// ─────────────────────────────────────────────────────────────────────────────
// BlogPostPage.jsx — uses shared components
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import { blogPosts, getPostBySlug, formatDate } from '@data/blogPosts'
import PageHero from '@components/common/PageHero'
import Badge from '@components/common/Badge'
import Button from '@components/common/Button'
import SectionHeading from '@components/common/SectionHeading'
import BlogCard from '@components/common/BlogCard'
import SEOHead from '@components/common/SEOHead'

// ─────────────────────────────────────────────────────────────────────────────
// Inline markdown renderer
// Handles: **bold heading** on its own line → <h3>
//          **bold** inline → <strong>
//          *italic* → <em>
//          --- → <hr>
//          plain paragraphs → <p>
// ─────────────────────────────────────────────────────────────────────────────
function renderInline(text) {
  // Split on **...** and *...* tokens
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if (part.startsWith('*') && part.endsWith('*')) {
      return <em key={i}>{part.slice(1, -1)}</em>
    }
    return part
  })
}

function renderContent(content) {
  if (!content) return null

  const blocks = content.split('\n\n').map(b => b.trim()).filter(Boolean)

  return blocks.map((block, i) => {

    // Horizontal rule
    if (block === '---') {
      return (
        <hr key={i} style={{
          border: 'none',
          borderTop: '1px solid #e0dfda',
          margin: '36px 0',
        }} />
      )
    }

    // Standalone **Heading** line → render as h3
    if (/^\*\*[^*]+\*\*$/.test(block)) {
      return (
        <h3 key={i} style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '1.15rem',
          fontWeight: 700,
          color: '#007935',
          margin: '36px 0 12px',
          letterSpacing: '0.01em',
          lineHeight: 1.35,
        }}>
          {block.slice(2, -2)}
        </h3>
      )
    }

    // Italic-only block (used for source credit paragraph)
    if (block.startsWith('*') && block.endsWith('*') && !block.startsWith('**')) {
      return (
        <p key={i} style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '0.875rem',
          color: '#88877f',
          fontStyle: 'italic',
          lineHeight: 1.7,
          margin: '0 0 20px',
        }}>
          {renderInline(block.slice(1, -1))}
        </p>
      )
    }

    // Regular paragraph (may contain inline **bold** / *italic*)
    return (
      <p key={i} style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '1rem',
        color: '#44443e',
        lineHeight: 1.85,
        margin: '0 0 20px',
      }}>
        {renderInline(block)}
      </p>
    )
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Get related posts — same category first, then fill from others
// ─────────────────────────────────────────────────────────────────────────────
function getRelated(currentPost, count = 3) {
  const same = blogPosts.filter(p => p.id !== currentPost.id && p.category === currentPost.category)
  const others = blogPosts.filter(p => p.id !== currentPost.id && p.category !== currentPost.category)
  return [...same, ...others].slice(0, count)
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function BlogPostPage() {
  const { slug } = useParams()
  const post = getPostBySlug(slug)

  useEffect(() => { window.scrollTo(0, 0) }, [slug])

  if (!post) return <Navigate to="/blog" replace />

  const related = getRelated(post, 3)

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    author: { '@type': 'Organization', name: 'Salient Agrinutrition' },
    publisher: {
      '@type': 'Organization',
      name: 'Salient Agrinutrition',
      url: 'https://salientagrinutrition.com',
    },
    datePublished: post.date,
    url: `https://salientagrinutrition.com/blog/${post.slug}`,
  }

  return (
    <main>
      <SEOHead
        title={post.title}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={articleSchema}
      />

      {/* ── PageHero ── */}
      <PageHero
        title={post.title}
        breadcrumbs={[
          { label: 'Home', to: '/' },
          { label: 'News & Blog', to: '/blog' },
          { label: post.title },
        ]}
      />


      {/* ── Article ── */}
      <article style={{ background: '#ffffff', padding: '56px 0 72px' }}>
        <div style={{
          maxWidth: '780px',
          margin: '0 auto',
          padding: '0 var(--container-padding)',
        }}>

          {/* Category badge */}
          <div style={{ marginBottom: '16px' }}>
            <Badge variant="outline">{post.category}</Badge>
          </div>

          {/* ── Featured image ── */}
          {post.image && (
            <div style={{
              width: '100%',
              height: 'clamp(220px, 32vw, 420px)',
              overflow: 'hidden',
              position: 'relative',
              background: 'linear-gradient(135deg, #2d5c3e, #3d7a55)',
              marginBottom: '32px',
            }}>
              <img
                src={post.image}
                alt={post.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
          )}

          {/* Title */}
          <h1 style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
            fontWeight: 400,
            letterSpacing: '0.02em',
            color: '#1a1a18',
            lineHeight: 1.25,
            margin: '0 0 18px',
          }}>
            {post.title}
          </h1>

          {/* Author | Date | ReadTime */}
          <div style={{
            display: 'flex', alignItems: 'center',
            gap: '16px', flexWrap: 'wrap',
            paddingBottom: '20px',
            borderBottom: '2px solid #007935',
            marginBottom: '32px',
          }}>
            <span style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '13.5px', fontWeight: 700,
              color: '#1a1a18',
            }}>
              {post.author}
            </span>
            <Pipe />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#88877f' }}>
              {formatDate(post.date)}
            </span>
            <Pipe />
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: '#88877f' }}>
              {post.readTime}
            </span>
          </div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '32px' }}>
              {post.tags.map(tag => (
                <Badge key={tag} variant="green" size="sm">{tag}</Badge>
              ))}
            </div>
          )}

          {/* ── Rendered body ── */}
          <div>
            {post.content
              ? renderContent(post.content)
              : <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', color: '#44443e', lineHeight: 1.85 }}>{post.excerpt}</p>
            }
          </div>

          {/* ── Source credit (if present in data) ── */}
          {post.sourceCredit && (
            <div style={{
              marginTop: '40px',
              padding: '20px 24px',
              background: '#f4faf6',
              borderLeft: '4px solid #007935',
              borderRadius: '0 8px 8px 0',
            }}>
              <p style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.8rem',
                fontWeight: 700,
                color: '#007935',
                margin: '0 0 6px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}>
                Source / Further Reading
              </p>
              <a
                href={post.sourceCredit.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '0.875rem',
                  color: '#44443e',
                  textDecoration: 'underline',
                  lineHeight: 1.55,
                }}
              >
                {post.sourceCredit.title}
              </a>
            </div>
          )}

          {/* ── Back button ── */}
          <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid #e8e8e8' }}>
            <Button
              variant="outline-green"
              to="/blog"
              showArrow={false}
              icon={
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M11 7H2M5.5 3.5L2 7l3.5 3.5"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              }
            >
              Back to News & Blog
            </Button>
          </div>

        </div>
      </article>

      {/* ── Related posts ── */}
      {related.length > 0 && (
        <section style={{ background: '#f9f9f7', padding: '56px 0 72px' }}>
          <div style={{
            maxWidth: 'var(--container-max)', margin: '0 auto',
            padding: '0 var(--container-padding)',
          }}>
            <div style={{ marginBottom: '36px' }}>
              <SectionHeading title="Related Articles" />
            </div>
            <div
              className="related-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '28px',
                alignItems: 'start',
              }}
            >
              {related.map((p, i) => (
                <BlogCard key={p.id} post={p} index={i} variant="compact" />
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        @media (max-width: 767px)                         { .related-grid { grid-template-columns: 1fr !important; } }
        @media (min-width: 768px) and (max-width: 1023px) { .related-grid { grid-template-columns: 1fr 1fr !important; } }
      `}</style>
    </main>
  )
}

const Pipe = () => (
  <span style={{ color: '#e0dfda', fontWeight: 300 }}>|</span>
)